import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import {
    Avatar, Divider,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    Paper,
    Subheader, TextField,
} from 'material-ui';
import ContentAdd from 'material-ui/svg-icons/content/add';
import Image from 'material-ui/svg-icons/image/image';
import withWidth from 'material-ui/utils/withWidth';
import { black, white } from 'material-ui/styles/colors';
import styles from './MediaBlocs.css';
import { handleError } from '../../../store/infos/actions';
import style from '../../../styles/styles';
import MediasService from '../../../services/medias';
import withTranslation from '../../../hoc/withTranslation';
import RaisedButton from 'material-ui/RaisedButton';

const maxFileSize = 268435456;

const titleStyle = {
    margin: '3px 30px',
    width: '100%',
    fontWeight: 400,
};
const inputStyle = {
    margin: '3px 30px 30px',
    width: 'calc(100% - 60px)',
};

export class MediaBlocs extends React.Component {

    static propTypes = {
        app: PropTypes.object.isRequired,
        medias: PropTypes.arrayOf(PropTypes.object).isRequired,
        postMedia: PropTypes.func.isRequired,
        selectMime: PropTypes.func.isRequired,
        entity: PropTypes.object.isRequired,
        pendingMedias: PropTypes.arrayOf(PropTypes.object).isRequired,
        uploadedMedias: PropTypes.arrayOf(PropTypes.object).isRequired,
        failedMedias: PropTypes.arrayOf(PropTypes.object).isRequired,
        currentLang: PropTypes.string,
    };

    state = {
        uploadingFiles: [],
        link: '',
    };

    // Handlers

    handleAddMedia = () => {
        this.dropzoneRef.open();
    };

    handleSaveMediaLink = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.state.link !== '') {
            this.props.postMedia(
                this.props.app.id,
                this.props.entity.id,
                this.state.link,
                this.props.currentLang,
            )
                .then(() => {
                    this.setState({ link: '' });
                });
        }
    };
    handleMediaDrop = (accepted, rejected) => {
        this.setState({ uploadingFiles: [] });

        accepted.forEach((media) => {
            if (media.size > maxFileSize) {
                this.props.handleError(
                    this.props.t('generic.error_upload_max_filesize'),
                );
            } else {
                console.log('Uploading file ' + media.name);
                this.props.postMedia(this.props.app.id,
                    this.props.entity.id,
                    media,
                    this.props.currentLang,
                    (progressEvent) => {
                        const percentCompleted = Math.round((
                            progressEvent.loaded * 100
                        ) / progressEvent.total);
                        const uploadingState = this.state.uploadingFiles.filter((mediaPercent) => mediaPercent.media
                            !== media);
                        console.log(media.name
                            + ' -> '
                            + percentCompleted
                            + '% ('
                            + progressEvent.loaded
                            + ' / '
                            + progressEvent.total
                            + ')');
                        this.setState({
                            uploadingFiles: [
                                ...uploadingState,
                                {
                                    media: media,
                                    percent: percentCompleted,
                                },
                            ],
                        });
                    },
                );
            }
        });
    };

    // Render

    renderHeader() {
        return (
            <Subheader style={{ ...style.subHeader, ...style.relative }}>
                {this.props.t('medias.media_plural')}
                <IconButton
                    style={styles.rightIcon}
                    tooltip={this.props.t('medias.new')}
                    tooltipPosition="top-center"
                    onClick={this.handleAddMedia}
                >
                    <ContentAdd color={white} />
                </IconButton>
            </Subheader>
        );
    }

    renderMediasTypes() {
        let mediasByType = MediasService.sortMediasByShortMimeType(this.props.medias);
        let keys = Object.keys(mediasByType);
        let items = keys.map((shortMime, index) => {
            let image = MediasService.imageForShortMime(shortMime);
            let displayName = MediasService.displayNameForShortMime(shortMime);
            let itemCount = mediasByType[shortMime].length;
            let countLabel = this.props.t('generic.item_count', { count: itemCount });

            return (
                <div key={shortMime}>
                    <ListItem
                        onClick={() => this.props.selectMime(shortMime)}
                        primaryText={displayName}
                        secondaryText={countLabel}
                        leftAvatar={<Avatar icon={image} backgroundColor={black} />}
                    />
                    {index !== (
                        keys.length - 1
                    ) && <Divider inset={true} />}
                </div>
            );

        });

        return (
            <List>
                {items}
            </List>
        );
    }

    renderDropzone() {
        return (
            <Dropzone
                ref={(node) => {
                    this.dropzoneRef = node;
                }}
                style={styles.dropzone.container}
                accept={MediasService.supportedMimeTypes().toString()}
                onDrop={this.handleMediaDrop}
            >
                <div style={styles.title}>
                    {this.props.t('medias.dropzone_hint')}
                </div>
                <div style={styles.subtitle}>
                    image, video, audio, text, csv, pdf, json, xml, zip, tar
                </div>
                <br />
                <Image />
            </Dropzone>
        );
    }

    renderPendingUploads() {
        return (
            <div style={styles.dropzone.container}>
                <div style={styles.title}>Uploading {this.state.uploadingFiles.length} files</div>
                <br />
                <Image />
                <br />
                {this.state.uploadingFiles
                    .sort((left, right) => {
                        return left.media.name < right.media.name;
                    })
                    .map((mediaPercent) => {
                        const media = mediaPercent.media;
                        return (
                            <div style={styles.dropzone.loading} key={media.name}>
                                <div style={styles.subtitle}>
                                    {media.name} - {(
                                    media.size / 1000 / 1000
                                ).toFixed(2)}Mo
                                    ({mediaPercent.percent}%)
                                </div>
                                <LinearProgress mode="determinate" value={mediaPercent.percent} />
                            </div>
                        );
                    })}
            </div>
        );
    }

    render() {
        return (
            <Paper>
                {this.renderHeader()}

                {this.props.medias.length > 0 && this.renderMediasTypes()}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: '100%',
                        marginTop: '30px'
                    }}
                >
                    <h3 style={titleStyle}>
                        {this.props.t('medias.new')}
                    </h3>
                    <h4 style={titleStyle}>
                        {this.props.t('medias.add_file')}
                    </h4>
                    <div style={inputStyle}>
                        {this.props.pendingMedias.length === 0 && this.renderDropzone()}
                        {this.props.pendingMedias.length > 0 && this.renderPendingUploads()}
                    </div>
                    <h4 style={titleStyle}>
                        {this.props.t('medias.add_link')}
                    </h4>
                    <div style={inputStyle}>
                            <TextField
                                hintText="Lien"
                                id="media-link"
                                value={this.state.link}
                                onChange={(event) => this.setState({ link: event.target.value })}
                                fullWidth={true}
                                multiLine={false}
                                style={{ width: '100%' }}
                            />
                            <RaisedButton
                                //style={styles.formButton}
                                label={this.props.t('generic.save')}
                                disabled={this.props.pendingMedias.length > 0}
                                type="button"
                                onClick={this.handleSaveMediaLink}
                                primary={true}
                            />
                    </div>
                </div>
            </Paper>
        );
    }
}

export default connect(null, { handleError })(withWidth()(withTranslation(MediaBlocs)));



// WEBPACK FOOTER //
// src/components/media/MediaBlocs/MediaBlocs.js